import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, Cell, ScatterChart, Scatter } from 'recharts';
import { TrendingUp, TrendingDown, Minus, Download, Calendar, Activity, Ear, Clock, Settings, Award } from 'lucide-react';
import api from '@/services/api';
import { apiClient, API_BASE_URL } from '@/services/httpClient';
import userManager from '@/services/userManager';
import { useAuth } from '@/contexts/AuthContext';
import { useTherapyTimer } from '@/hooks/useTherapyTimer';
import PageHeader from '@/components/PageHeader';
import CoachProgressCard from '../components/CoachProgressCard';
import HelpIcon from '@/components/HelpIcon';
import PremiumUpsellScreen from '@/components/PremiumUpsellScreen';

const Progress = ({ t }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { sessionElapsedSeconds, isActive: timerActive } = useTherapyTimer();
  
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [timeseries, setTimeseries] = useState(null);
  const [windowDays, setWindowDays] = useState(90);
  const [showTrendline, setShowTrendline] = useState(false); // Toggle for trendline (default OFF)
  const [showVariationHint, setShowVariationHint] = useState(false); // Mini-onboarding for variations
  
  // Language setting
  const lang = localStorage.getItem('hearen_lang') || 'fr';
  
  // Calculate todayTotalMinutes: ONLY from backend completed sessions
  // CORRECTION: Ne plus utiliser le timer local pour éviter l'incrémentation continue
  const todayTotalMinutes = React.useMemo(() => {
    let completedMinutes = 0;
    
    // Get completed sessions from backend (source unique de vérité)
    if (timeseries && timeseries.minutes_per_day && timeseries.minutes_per_day.length > 0) {
      const today = new Date().toISOString().split('T')[0];
      const todayEntry = timeseries.minutes_per_day.find(entry => 
        entry.date.startsWith(today)
      );
      completedMinutes = todayEntry ? Math.round(todayEntry.value) : 0;
    }
    
    // Ne plus ajouter la session en cours - affichage figé basé sur les données enregistrées
    // Le temps réel est affiché dans le Player, pas dans cette carte
    return completedMinutes;
  }, [timeseries]);
  
  // Calculate trendline using linear regression
  const calculateTrendline = (data) => {
    if (!data || data.length < 2) return [];
    
    const n = data.length;
    let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
    
    data.forEach((point, index) => {
      const x = index;
      const y = point.value || 0;
      sumX += x;
      sumY += y;
      sumXY += x * y;
      sumXX += x * x;
    });
    
    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    
    return data.map((point, index) => ({
      date: point.date,
      trendline: slope * index + intercept
    }));
  };
  
  // Calculate interpretive summary
  const calculateSummary = (data) => {
    if (!data || data.length < 7) {
      return { status: 'stable', percentage: 0 };
    }
    
    // Current week average (last 7 points)
    const currentWeek = data.slice(-7);
    const currentAvg = currentWeek.reduce((sum, p) => sum + (p.value || 0), 0) / 7;
    
    // Previous week average (7 points before)
    if (data.length < 14) {
      return { status: 'stable', percentage: 0 };
    }
    
    const previousWeek = data.slice(-14, -7);
    const previousAvg = previousWeek.reduce((sum, p) => sum + (p.value || 0), 0) / 7;
    
    const percentageChange = ((currentAvg - previousAvg) / previousAvg) * 100;
    
    let status = 'stable';
    if (percentageChange <= -10) {
      status = 'improving';
    } else if (percentageChange >= 10) {
      status = 'increasing';
    }
    
    return { status, percentage: Math.abs(percentageChange).toFixed(0) };
  };

  // 🔒 PREMIUM ONLY - Bloquer l'accès FREE
  const isFreeUser = !user?.isPremium && user?.subscription !== 'premium';
  
  // Auto-refresh when timer becomes inactive (session just stopped)
  const [wasActive, setWasActive] = useState(false);
  
  useEffect(() => {
    if (!isFreeUser) {
      loadProgressData();
    }
  }, [windowDays, isFreeUser]);
  
  useEffect(() => {
    if (isFreeUser) return;
    if (wasActive && !timerActive) {
      // Timer just stopped, reload progress data to get the latest session
      setTimeout(() => {
        loadProgressData();
      }, 1000); // Small delay to ensure backend has processed the session
    }
    setWasActive(timerActive);
  }, [timerActive, isFreeUser, wasActive]);

  // CONDITIONAL RETURN AFTER ALL HOOKS
  if (isFreeUser) {
    return (
      <PremiumUpsellScreen
        title={lang === 'en' ? 'Unlock Your Progress Tracking' : 'Débloquez Votre Suivi des Progrès'}
        subtitle={lang === 'en' 
          ? 'Access your statistics, trends, evolution score, and personalized Hearen Assistant.'
          : 'Accédez à vos statistiques, vos tendances, votre score d\'évolution et au coaching IA personnalisé.'
        }
        language={lang}
        icon={Activity}
      />
    );
  }

  const loadProgressData = async () => {
    try {
      setLoading(true);
      const userId = await userManager.getUserId();
      
      // Add cache-busting timestamp to force fresh data
      const timestamp = new Date().getTime();
      
      // ⭐ V3 iOS FIX: Use apiClient instead of direct fetch
      // This ensures correct API_BASE_URL resolution on iOS Capacitor
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache'
      };
      
      console.log('[Progress] Loading progress data with API_BASE_URL:', API_BASE_URL);
      
      const [summaryRes, timeseriesRes] = await Promise.all([
        apiClient.get(`/api/progress/summary?userId=${userId}&windowDays=${windowDays}&_t=${timestamp}`, { headers }),
        apiClient.get(`/api/progress/timeseries?userId=${userId}&_t=${timestamp}`, { headers })
      ]);
      
      const summaryData = summaryRes.data;
      const timeseriesData = timeseriesRes.data;
      
      setSummary(summaryData);
      setTimeseries(timeseriesData);
      
      // Check if user has seen the variation hint
      const hasSeenHint = localStorage.getItem('hearen-seen-variation-hint');
      if (!hasSeenHint) {
        setShowVariationHint(true);
      }
    } catch (error) {
      console.error('Error loading progress data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDismissVariationHint = () => {
    localStorage.setItem('hearen-seen-variation-hint', 'true');
    setShowVariationHint(false);
  };

  // Export PDF feature disabled for now
  // const handleExportPDF = () => {
  //   alert(t?.exportPDFSoon || 'PDF export coming soon');
  // };

  const getTrendIcon = (trend) => {
    switch(trend) {
      case 'improving':
        return <TrendingUp className="w-6 h-6 text-green-600" />;
      case 'attention':
        return <TrendingUp className="w-6 h-6 text-blue-600" />;
      default:
        return <TrendingUp className="w-6 h-6 text-orange-600" />;
    }
  };

  const getTrendText = (trend) => {
    switch(trend) {
      case 'improving': return t?.trendImproving || 'Great progress!';
      case 'attention': return t?.trendAttention || 'Making progress - Keep going!';
      default: return t?.trendStable || 'Positive evolution';
    }
  };

  const getTrendColor = (trend) => {
    switch(trend) {
      case 'improving': return 'text-green-600 bg-green-50';
      case 'attention': return 'text-blue-600 bg-blue-50';
      default: return 'text-orange-600 bg-orange-50';
    }
  };

  const getInsightIcon = (type) => {
    switch(type) {
      case 'positive': return '✅';
      case 'recommendation': return '💡';
      default: return 'ℹ️';
    }
  };

  const getBarColor = (value, maxValue) => {
    // Color gradient from purple (low) to green (high)
    // Based on percentage of max value
    const percentage = maxValue > 0 ? (value / maxValue) : 0;
    
    if (percentage >= 0.75) return '#10b981'; // Green - excellent
    if (percentage >= 0.5) return '#3b82f6';  // Blue - good
    if (percentage >= 0.25) return '#f59e0b'; // Orange - moderate
    return '#9333ea'; // Purple - low
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0F1C] pt-20 px-4 pb-4">
        <PageHeader 
          title={t?.progress || "Progress Tracking"}
          onBack={() => navigate('/home')}
        />
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#6F42EA] mb-4"></div>
            <p className="text-white/60">
              {t?.loading || 'Loading...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!summary || !timeseries) {
    return (
      <div className="min-h-screen bg-[#0A0F1C] pt-20 px-4 pb-4">
        <PageHeader 
          title={t?.progress || "Progress Tracking"}
          onBack={() => navigate('/home')}
        />
        <div className="max-w-4xl mx-auto mt-8">
          <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-3xl shadow-[0_10px_40px_rgba(0,0,0,0.55)] p-8 text-center">
            <p className="text-white/70">
              {t?.noData || 'No data available. Complete tests and therapy sessions to see your progress.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0F1C] pt-20 px-4 pb-24 relative overflow-hidden">
      {/* Halo premium top-left */}
      <div 
        className="absolute -top-32 -left-20 w-[320px] h-[320px] bg-[#6F42EA] opacity-[0.12] blur-[130px] pointer-events-none"
      />
      
      {/* Halo premium bottom-right */}
      <div 
        className="absolute bottom-0 right-[-40px] w-[280px] h-[280px] bg-[#3B48EE] opacity-[0.10] blur-[140px] pointer-events-none"
      />

      {/* Grain texture ultra léger */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          mixBlendMode: 'overlay'
        }}
      />

      <div className="relative z-10">
      <PageHeader 
        title={t?.progress || "Progress Tracking"}
        onBack={() => navigate('/home')}
      />

      {/* Header Section with Global Progress Score Premium */}
      <div className="max-w-6xl mx-auto mt-6 mb-6">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#3B48EE]/40 to-[#6F42EA]/30 backdrop-blur-[8px] border border-white/15 shadow-[0_0_45px_rgba(120,90,255,0.65)] px-6 py-5">
          {/* Overlay gradient subtil */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent pointer-events-none" />
          
          <div className="relative z-10">
            {/* Score Section */}
            <div>
              {/* Title with icon */}
              <div className="flex items-center gap-2 mb-3">
                <Award className="text-white" size={20} />
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-white text-base tracking-wide">
                    {t?.globalProgressScore || 'Global Progress Score'}
                  </h3>
                  <HelpIcon 
                    title={t?.globalProgressScore || 'Global Progress Score'}
                    content={
                      <p>{t?.globalProgressScoreTooltip || 
                        'This score represents your long-term listening trends over several weeks or months. It is based on trends in: your sessions, your therapy minutes, your self-reported discomfort evolution, your consistency over time. It reflects your long-term progress.'}</p>
                    }
                    size={18}
                  />
                </div>
              </div>
              
              {/* Score value and trend - BUILD 52: Fix truncation on iPhone */}
              <div className="flex flex-wrap items-center gap-4 sm:gap-6">
                <div className="text-4xl sm:text-5xl font-bold text-white tracking-wide drop-shadow-[0_6px_20px_rgba(0,0,0,0.65)]">
                  {summary.score}
                  <span className="text-xl sm:text-2xl opacity-60 ml-1">/100</span>
                </div>
                
                {/* Trend badge premium - wrap on small screens */}
                <div className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl bg-white/20 backdrop-blur-md border border-white/20 min-w-0">
                  {getTrendIcon(summary.kpis.trend)}
                  <span className="font-semibold text-white text-sm sm:text-base truncate">
                    {getTrendText(summary.kpis.trend)}
                  </span>
                </div>
              </div>
              
              {/* Subtitle */}
              <div className="text-xs text-white/75 mt-2">
                {t?.globalProgressScoreSubtitle || 'Your overall evolution based on long-term trends.'}
              </div>
            </div>

            {/* Period Selector Premium - BUILD 52: Fix wrap on iPhone */}
            <div className="mt-4 flex flex-wrap gap-2">
              {[30, 90, 180, 365].map(days => (
                <button
                  key={days}
                  onClick={() => setWindowDays(days)}
                  className={`px-3 py-1.5 rounded-xl text-xs sm:text-sm transition-all ${
                    windowDays === days
                      ? 'bg-white/10 backdrop-blur-md border border-white/10 text-white font-semibold hover:bg-white/20 active:scale-[0.97]'
                      : 'bg-white/5 backdrop-blur-md border border-white/10 text-white/80 hover:bg-white/15'
                  }`}
                >
                  {days} {t?.days || 'days'}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Separator Premium */}
      <div className="max-w-6xl mx-auto my-8">
        <div className="border-t border-white/10"></div>
      </div>

      {/* Coach Progress Card - Weekly Score */}
      <div className="max-w-6xl mx-auto mb-6">
        <CoachProgressCard t={t} />
      </div>

      {/* Introductory Banner Premium */}
      <div className="max-w-6xl mx-auto mb-6">
        <div className="relative overflow-hidden rounded-3xl bg-white/5 backdrop-blur-[8px] border border-white/10 shadow-[0_10px_40px_rgba(0,0,0,0.55)] p-6">
          {/* Halo derrière le header */}
          <div className="absolute left-1/2 -translate-x-1/2 -top-2 w-[160px] h-[160px] bg-white/10 blur-[90px] opacity-[0.15] rounded-full pointer-events-none" />
          
          {/* Overlay gradient subtil */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#3B48EE]/15 via-transparent to-[#6F42EA]/10 pointer-events-none" />
          
          <div className="relative">
            <h2 className="text-2xl font-bold text-white mb-2 tracking-wide drop-shadow-[0_6px_16px_rgba(255,255,255,0.10)]">
              👋 {t?.progressBannerTitle || 'Your Progress Overview'}
            </h2>
            <p className="text-white/70 text-base mb-3 tracking-wide leading-relaxed">
              {t?.progressBannerDesc || 'Track your therapy time, tinnitus perception and daily wellbeing evolution.'}
            </p>
            <div className="rounded-2xl p-3 bg-[#3B48EE]/20 backdrop-blur-md border border-[#3B48EE]/30">
              <p className="text-sm text-white/80 leading-relaxed">
                {t?.progressBannerExplain || 'Hearen tracks two different types of data: your therapy activity (objective) and your tinnitus perception (subjective). These indicators are not computed from each other.'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Lab Mode Card Premium - Build 52: Fixed mobile responsive layout */}
      <div className="max-w-6xl mx-auto mb-6 px-4 sm:px-0">
        <div 
          className="relative overflow-hidden rounded-3xl bg-white/5 backdrop-blur-[8px] border border-[#8B3EEC]/30 shadow-[0_10px_40px_rgba(139,62,236,0.35)] p-4 sm:p-6 cursor-pointer hover:scale-[1.01] transition-all"
          onClick={() => navigate('/progress/plan')}
        >
          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#8B3EEC]/15 via-transparent to-[#6F42EA]/10 pointer-events-none" />
          
          {/* Mobile: Stack vertically, Desktop: Side by side */}
          <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <div className="w-12 h-12 sm:w-14 sm:h-14 flex-shrink-0 bg-[#8B3EEC]/20 backdrop-blur-md rounded-2xl flex items-center justify-center border border-[#8B3EEC]/30">
                <Settings size={24} className="text-[#8B3EEC] sm:w-7 sm:h-7" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h3 className="text-base sm:text-xl font-bold text-white tracking-wide">{t?.labTitle || 'Lab Mode (Experimental)'}</h3>
                  <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-[#8B3EEC] text-white text-xs font-semibold rounded-full whitespace-nowrap">
                    {t?.labBeta || 'Beta'}
                  </span>
                </div>
                <p className="text-white/70 text-xs sm:text-sm line-clamp-2">{t?.labDescription || 'Experimental Notch/Inverse Notch Programs'}</p>
              </div>
            </div>
            <button className="flex-shrink-0 bg-gradient-to-br from-[#8B3EEC] to-[#6F42EA] hover:brightness-110 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl text-sm sm:text-base font-semibold shadow-[0_0_24px_rgba(139,62,236,0.5)] transition-all active:scale-[0.97] w-full sm:w-auto text-center">
              {t?.labViewPlan || 'View full plan'} →
            </button>
          </div>
        </div>
      </div>

      {/* KPIs Grid Premium */}
      <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {/* CARD 1 - Days Active Premium */}
        <div 
          className="relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-[6px] border border-white/10 shadow-[0_8px_30px_rgba(0,0,0,0.45)] px-4 py-4"
          style={{
            animation: 'fadeSlideUp 200ms ease-out'
          }}
        >
          <div className="absolute top-2 right-2">
            <HelpIcon 
              title={t?.kpiDaysActive || 'Days Active'}
              content={
                <p>{t?.kpiDaysActiveTooltip || 'Total number of days since you started using Hearen. This counter reflects your engagement and consistency with the therapy program.'}</p>
              }
              size={16}
            />
          </div>
          <div className="flex flex-col items-center text-center gap-3 pt-2">
            <div className="p-2 bg-white/10 backdrop-blur-md rounded-xl border border-white/15">
              <Calendar className="w-6 h-6 text-white/80" />
            </div>
            <div className="text-3xl font-bold text-white tracking-wide drop-shadow-[0_4px_12px_rgba(0,0,0,0.45)]">{summary.kpis.therapy_days}</div>
            <div className="text-sm md:text-base text-white font-bold leading-tight">
              {t?.kpiDaysActive || 'Days Active'}
            </div>
            <div className="text-xs text-white/70">
              {t?.kpiDaysActiveSubtext || 'Since you started'}
            </div>
          </div>
        </div>

        {/* CARD 2 - Therapy Time Premium */}
        <div 
          className="relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-[6px] border border-white/10 shadow-[0_8px_30px_rgba(0,0,0,0.45)] px-4 py-4"
          style={{
            animation: 'fadeSlideUp 200ms ease-out 50ms both'
          }}
        >
          <div className="absolute top-2 right-2">
            <HelpIcon 
              title={t?.kpiTherapyTime || "Temps d'écoute"}
              content={
                <p>{t?.kpiTherapyTimeTooltip || "Durée totale d'écoute complétée aujourd'hui (ou cette semaine). Cela représente le temps cumulé passé à utiliser les paysages sonores, la radio et les playlists."}</p>
              }
              size={16}
            />
          </div>
          <div className="flex flex-col items-center text-center gap-3 pt-2">
            <div className="p-2 bg-white/10 backdrop-blur-md rounded-xl border border-white/15">
              <Clock className="w-6 h-6 text-white/80" />
            </div>
            <div className="text-3xl font-bold text-white tracking-wide drop-shadow-[0_4px_12px_rgba(0,0,0,0.45)]">
              {todayTotalMinutes}
            </div>
            <div className="text-sm md:text-base text-white font-bold leading-tight">
              {t?.kpiTherapyTime || 'Temps de Thérapie sonore'}
            </div>
            <div className="text-xs text-white/70">
              {t?.kpiTherapyTimeSubtext || 'minutes aujourd\'hui'}
            </div>
          </div>
        </div>

        {/* CARD 3 - VAS Premium */}
        {summary.kpis.vas_current !== null && (
          <div 
            className="relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-[6px] border border-white/10 shadow-[0_8px_30px_rgba(0,0,0,0.45)] px-4 py-4"
            style={{
              animation: 'fadeSlideUp 200ms ease-out 100ms both'
            }}
          >
            <div className="absolute top-2 right-2">
              <HelpIcon 
                title={t?.kpiVASPerceived || 'VAS Perceived'}
                content={
                  <p>{t?.kpiVASPerceivedTooltip || 'Your VAS (Visual Analog Scale) score: a subjective discomfort scale from 0 (no discomfort) to 10 (very strong discomfort). This reflects how you perceive your tinnitus on a daily basis.'}</p>
                }
                size={16}
              />
            </div>
            <div className="flex flex-col items-center text-center gap-3 pt-2">
              <div className="p-2 bg-white/10 backdrop-blur-md rounded-xl border border-white/15">
                <Activity className="w-6 h-6 text-white/80" />
              </div>
              <div className="text-3xl font-bold text-white tracking-wide drop-shadow-[0_4px_12px_rgba(0,0,0,0.45)]">
                {summary.kpis.vas_current}<span className="text-lg text-white/60">/10</span>
              </div>
              <div className="text-sm md:text-base text-white font-bold leading-tight">
                {t?.kpiVASPerceived || 'VAS'}
              </div>
              <div className="text-xs text-white/70">
                {summary.kpis.vas_baseline 
                  ? `${t?.kpiVASPrevious || 'Previous:'} ${summary.kpis.vas_baseline}/10`
                  : (t?.kpiVASPerceivedSubtext || '(perceived intensity)')}
              </div>
            </div>
          </div>
        )}

        {/* CARD 4 - Tinnitus Pitch & Volume Premium */}
        {summary.kpis.tinnitus_frequency && (
          <div 
            className="relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-[6px] border border-white/10 shadow-[0_8px_30px_rgba(0,0,0,0.45)] px-4 py-4"
            style={{
              animation: 'fadeSlideUp 200ms ease-out 150ms both'
            }}
          >
            <div className="absolute top-2 right-2">
              <HelpIcon 
                title={t?.kpiTinnitusPitch || 'Tinnitus Pitch'}
                content={
                  <p>{t?.kpiTinnitusPitchTooltip || 'The dominant pitch (frequency) of your tinnitus, measured in Hertz (Hz), and perceived volume in dB SPL. These values are determined during your tinnitus characterization tests and help personalize your therapy.'}</p>
                }
                size={16}
              />
            </div>
            <div className="flex flex-col items-center text-center gap-2 pt-2">
              <div className="p-2 bg-white/10 backdrop-blur-md rounded-xl border border-white/15">
                <Ear className="w-6 h-6 text-white/80" />
              </div>
              <div className="text-3xl font-bold text-white tracking-wide drop-shadow-[0_4px_12px_rgba(0,0,0,0.45)]">
                {summary.kpis.tinnitus_frequency.toFixed(0)} <span className="text-base font-bold">Hz</span>
              </div>
              {summary.kpis.mml_current !== null && summary.kpis.mml_current !== undefined && (
                <div className="text-xl font-semibold text-violet-300 tracking-wide">
                  {summary.kpis.mml_current.toFixed(0)} <span className="text-sm font-medium">dB SPL</span>
                </div>
              )}
              <div className="text-sm md:text-base text-white font-bold leading-tight">
                {t?.kpiTinnitusPitch || 'Fréquence Acouphènes'}
              </div>
              <div className="text-xs text-white/70">
                {summary.kpis.mml_baseline !== null && summary.kpis.mml_baseline !== undefined
                  ? `${t?.kpiVolumePrevious || 'Volume précédent:'} ${summary.kpis.mml_baseline.toFixed(0)} dB`
                  : (t?.kpiTinnitusPitchSubtext || 'Votre fréquence actuelle')}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* SECTION A - Your Therapy Activity Premium */}
      <div className="max-w-6xl mx-auto mb-8">
        <div className="relative mb-4">
          {/* Halo derrière le header */}
          <div className="absolute left-1/2 -translate-x-1/2 -top-2 w-[160px] h-[160px] bg-white/10 blur-[90px] opacity-[0.15] rounded-full pointer-events-none" />
          
          <h3 className="relative text-xl font-bold text-white mb-1 flex items-center gap-2 tracking-wide drop-shadow-[0_6px_16px_rgba(255,255,255,0.10)]">
            <span className="text-2xl">🕒</span> {t?.sectionTherapyActivity || "Votre Activité d'écoute"}
          </h3>
          <p className="text-sm text-white/70 tracking-wide">
            {t?.sectionTherapyActivityDesc || "Données objectives : vos sessions et temps d'écoute sonore"}
          </p>
        </div>
        
        {/* Minutes per Day Chart Premium */}
        {timeseries.minutes_per_day.length > 0 ? (
          <div className="relative overflow-hidden rounded-3xl bg-white/5 backdrop-blur-[8px] border border-white/10 shadow-[0_10px_40px_rgba(0,0,0,0.55)] px-5 py-5">
            {/* Overlay gradient subtil */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#3B48EE]/15 via-transparent to-[#6F42EA]/10 pointer-events-none" />
            
            <div className="relative">
              <div className="flex items-center justify-between mb-1">
                <h4 className="text-lg flex items-center gap-2 text-white font-semibold tracking-wide">
                  <span className="text-xl">🕒</span> {t?.therapyMinutesPerDay || "Minutes d'écoute par jour"}
                </h4>
                <HelpIcon 
                  title={t?.therapyMinutesPerDay || "Minutes d'écoute par jour"}
                  content={
                    <p>{t?.therapyMinutesHelpText || "Temps total d'écoute chaque jour. Objectif recommandé : 15 minutes, sans obligation."}</p>
                  }
                  size={18}
                />
              </div>
              <p className="text-white/70 text-sm mb-4 tracking-wide">
                {t?.therapyMinutesPerDayDesc || "Durée totale des sessions d'écoute complétées chaque jour."}
              </p>
              
              <div className="bg-[#0F1525] rounded-2xl border border-white/10 shadow-[0_6px_28px_rgba(0,0,0,0.45)] p-4">
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={timeseries.minutes_per_day}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12, fill: 'rgba(255,255,255,0.7)' }}
                      tickFormatter={(value) => new Date(value).toLocaleDateString(t?.locale || 'en-US', { month: 'short', day: 'numeric' })}
                      stroke="rgba(255,255,255,0.3)"
                    />
                    <YAxis 
                      tick={{ fontSize: 12, fill: 'rgba(255,255,255,0.7)' }}
                      stroke="rgba(255,255,255,0.3)"
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(15, 23, 42, 0.95)', 
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '12px',
                        padding: '12px'
                      }}
                      labelStyle={{ 
                        color: '#ffffff',
                        fontWeight: '600',
                        fontSize: '13px',
                        marginBottom: '4px'
                      }}
                      itemStyle={{
                        color: '#ffffff',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                      labelFormatter={(value) => new Date(value).toLocaleDateString(t?.locale || 'en-US', { month: 'long', day: 'numeric' })}
                      formatter={(value, name, props) => {
                        const minutes = Math.round(value);
                        
                        // Don't show tooltip for days with 0 minutes
                        if (minutes === 0) {
                          return [null, ''];  // Return null to hide tooltip content
                        }
                        
                        // Display minutes only
                        return [`${minutes} min`, ''];
                      }}
                    />
                    <Bar dataKey="value" radius={[8, 8, 0, 0]} fill="#8F6CFF">
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              </div>
              
              <div className="text-xs text-white/60 mt-3 text-center">
                {t?.therapyMinutesRecommendation || 'Recommended daily target: 15 minutes'}
              </div>
            </div>
          </div>
        ) : (
          <div className="relative overflow-hidden rounded-3xl bg-white/5 backdrop-blur-[8px] border border-white/10 shadow-[0_10px_40px_rgba(0,0,0,0.55)] px-5 py-6">
            <div className="text-center space-y-3">
              <div className="text-5xl opacity-30">📊</div>
              <h4 className="text-lg text-white/80 font-medium">
                {t?.noDataYet || 'Aucune donnée disponible'}
              </h4>
              <p className="text-sm text-white/60 max-w-md mx-auto">
                {t?.noDataDescription || "Commencez une session d'écoute d'au moins 10 secondes et cliquez sur STOP pour enregistrer vos premières données."}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* SECTION B - Your Tinnitus Perception Premium */}
      <div className="max-w-6xl mx-auto mb-8">
        <div className="relative mb-4">
          {/* Halo derrière le header */}
          <div className="absolute left-1/2 -translate-x-1/2 -top-2 w-[160px] h-[160px] bg-white/10 blur-[90px] opacity-[0.15] rounded-full pointer-events-none" />
          
          <h3 className="relative text-xl font-bold text-white mb-1 flex items-center gap-2 tracking-wide drop-shadow-[0_6px_16px_rgba(255,255,255,0.10)]">
            <span className="text-2xl">👂</span> {t?.sectionTinnitusPerception || 'Your Tinnitus Perception'}
          </h3>
          <p className="text-sm text-white/70 tracking-wide">
            {t?.sectionTinnitusPerceptionDesc || 'Subjective data: how you experience your tinnitus'}
          </p>
        </div>
        
        {/* Tinnitus Impact Score Chart Premium */}
        {timeseries.vas_series && timeseries.vas_series.length > 0 && (
          <div className="relative overflow-hidden rounded-3xl bg-white/5 backdrop-blur-[8px] border border-white/10 shadow-[0_10px_40px_rgba(0,0,0,0.55)] px-5 py-5 space-y-4">
            {/* Overlay gradient subtil */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#3B48EE]/15 via-transparent to-[#6F42EA]/10 pointer-events-none" />
            
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="text-lg flex items-center gap-2 text-white font-semibold tracking-wide">
                      <span className="text-xl">👂</span> {t?.tinnitusImpactScore || 'Tinnitus Impact Score'}
                    </h4>
                    {/* Variations info */}
                    <HelpIcon 
                      title={t?.tooltipVariationsTitle || 'Daily Variations Are Normal'}
                      content={
                        <div className="space-y-3">
                          <p className="font-semibold text-[#8B7FF5]">{t?.tooltipVariationsTitle || 'Daily variations are normal'}</p>
                          <p>{t?.tooltipVariationsText || 
                            'Your tinnitus can vary from day to day — this is completely normal. These fluctuations often depend on stress, sleep, fatigue, focus or ambient noise.'}</p>
                          <p className="font-medium">What matters most is your overall trend over several days, not a single day\'s value.</p>
                        </div>
                      }
                      size={20}
                    />
                  </div>
                  
                  {/* Short info premium */}
                  <p className="text-sm text-white/60 mb-2">
                    {t?.variationsInfoShort || 'Daily variations are normal. Focus on your overall trend.'}
                  </p>
                  
                  <p className="text-white/70 text-sm leading-relaxed">
                    {t?.tinnitusImpactScoreDesc || 'Your daily impact score (0 = low, 100 = high), based on your self-reported data.'}
                  </p>
                  
                  {/* Clarification pill premium */}
                  <div className="mt-3 p-3 bg-white/8 backdrop-blur-md border border-white/15 rounded-2xl">
                    <p className="text-sm font-medium text-white/85">
                      {t?.tinnitusImpactScoreWarning || 'This score reflects your daily tinnitus discomfort based on your own feedback. It is not linked to your therapy minutes.'}
                    </p>
                  </div>
                </div>
                
                <div className="ml-4">
                  <HelpIcon 
                    title={t?.tinnitusImpactEvolution || 'Tinnitus Impact Evolution'}
                    content={
                      <div className="space-y-3">
                        <p>{t?.tinnitusImpactTooltip || 
                          'This graph shows only your self-reported tinnitus discomfort, day by day. It does not include therapy minutes or any automatically generated calculation.'}</p>
                        <p>Each point reflects exactly what you selected when you rated your tinnitus for that day. The optional trendline simply helps visualize your general progression over time.</p>
                      </div>
                    }
                    size={20}
                  />
                </div>
              </div>
              
              <div>
              {/* Interpretive Summary */}
              {(() => {
                // Convert VAS series to impact scores (VAS * 10) for analysis
                const vasImpactData = timeseries.vas_series.map(point => ({
                  ...point,
                  value: point.value * 10
                }));
                const summary = calculateSummary(vasImpactData);
                const statusConfig = {
                  improving: {
                    icon: '⭐',
                    title: t?.summaryImproving || 'Improving',
                    text: (t?.summaryImprovingText || `Your average tinnitus impact is decreasing this week (-${summary.percentage}%).`).replace('{percentage}', summary.percentage),
                    bgColor: '#F0FDF4',
                    borderColor: '#BBF7D0',
                    textColor: '#166534'
                  },
                  stable: {
                    icon: '➖',
                    title: t?.summaryStable || 'Stable',
                    text: t?.summaryStableText || 'Your tinnitus perception is relatively stable this week.',
                    bgColor: '#EFF6FF',
                    borderColor: '#BFDBFE',
                    textColor: '#1e40af'
                  },
                  increasing: {
                    icon: '⚠️',
                    title: t?.summaryIncreasing || 'Increasing',
                    text: (t?.summaryIncreasingText || `Your tinnitus impact is higher this week (+${summary.percentage}%).`).replace('{percentage}', summary.percentage),
                    bgColor: '#FFF7ED',
                    borderColor: '#FED7AA',
                    textColor: '#9A3412'
                  }
                };
                const config = statusConfig[summary.status];
                
                return (
                  <div className="mb-4 p-3 bg-white/6 backdrop-blur-md border border-white/12 rounded-2xl">
                    <div className="font-semibold text-sm flex items-center gap-2 text-white">
                      <span className="bg-white/15 backdrop-blur-md rounded-full p-1.5">{config.icon}</span> {config.title}
                    </div>
                    <div className="text-xs mt-1 text-white/75">{config.text}</div>
                  </div>
                );
              })()}
              
              {/* Trendline Toggle Premium */}
              <div className="mb-3 flex items-center justify-end gap-2">
                <label className="flex items-center gap-2 cursor-pointer text-sm text-white/70">
                  <input
                    type="checkbox"
                    checked={showTrendline}
                    onChange={(e) => setShowTrendline(e.target.checked)}
                    className="w-4 h-4 text-[#6F42EA] rounded focus:ring-2 focus:ring-[#6F42EA]"
                  />
                  <span>{t?.showTrendline || 'Show trendline'}</span>
                </label>
              </div>
              
              {/* Graph premium */}
              <div className="bg-[#0F1525] rounded-2xl border border-white/10 shadow-[0_6px_28px_rgba(0,0,0,0.45)] p-4">
                <ResponsiveContainer width="100%" height={250}>
                <LineChart data={timeseries.vas_series.map((point, index) => {
                  // Convert VAS score (1-10) to Impact Score (0-100)
                  // VAS 1 (very mild) = Impact 10 (low impact)
                  // VAS 10 (severe) = Impact 100 (high impact)
                  const impactScore = point.value * 10;
                  
                  const convertedData = timeseries.vas_series.map(p => ({
                    ...p,
                    value: p.value * 10
                  }));
                  
                  const trendlineData = showTrendline ? calculateTrendline(convertedData) : [];
                  
                  return {
                    date: point.date,
                    value: impactScore,
                    trendline: trendlineData[index]?.trendline
                  };
                })}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12, fill: 'rgba(255,255,255,0.7)' }}
                    tickFormatter={(value) => new Date(value).toLocaleDateString(t?.locale || 'en-US', { month: 'short', day: 'numeric' })}
                    stroke="rgba(255,255,255,0.3)"
                  />
                  <YAxis 
                    domain={[0, 100]} 
                    tick={{ fontSize: 12, fill: 'rgba(255,255,255,0.7)' }}
                    stroke="rgba(255,255,255,0.3)"
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(255,255,255,0.1)', 
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '12px',
                      color: '#fff',
                      padding: '12px'
                    }}
                    labelStyle={{ color: 'rgba(255,255,255,0.8)' }}
                    labelFormatter={(value) => new Date(value).toLocaleDateString(t?.locale || 'en-US', { month: 'long', day: 'numeric' })}
                    formatter={(value, name) => {
                      if (name === 'value') return [`${t?.impactScore || 'Impact'}: ${value.toFixed(0)}/100`, ''];
                      if (name === 'trendline') return [`${t?.trendline || 'Trend'}: ${value.toFixed(0)}`, ''];
                      return [value, name];
                    }}
                  />
                  <Legend 
                    verticalAlign="bottom" 
                    height={36}
                    wrapperStyle={{ color: '#fff' }}
                    formatter={(value) => {
                      if (value === 'value') return t?.legendDailyImpact || 'Daily impact';
                      if (value === 'trendline') return t?.legendTrendline || 'Trendline';
                      return value;
                    }}
                  />
                  
                  {/* Main line - VAS Impact Score (User-Reported Data Only) */}
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    strokeWidth={3} 
                    stroke="#8F6CFF"
                    dot={{ fill: '#8F6CFF', r: 4 }}
                    activeDot={{ r: 6 }}
                    name="value"
                  />
                  
                  {/* Optional Trendline - Overall Direction */}
                  {showTrendline && (
                    <Line 
                      type="monotone" 
                      dataKey="trendline" 
                      strokeWidth={2} 
                      stroke="#60A5FA"
                      strokeDasharray="5 5"
                      dot={false}
                      name="trendline"
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
              </div>
              
              {/* Simplified Legend Text Premium */}
              <div className="text-xs text-white/60 mt-3 space-y-1 px-2">
                <div>• <span className="font-semibold text-[#8F6CFF]">{t?.legendPurpleLine || 'Purple line:'}</span> {t?.legendPurpleDesc || 'your self-reported daily tinnitus impact (0 = low, 100 = high)'}</div>
                {showTrendline && (
                  <div>• <span className="font-semibold text-blue-400">{t?.legendBlueLine || 'Blue dotted line:'}</span> {t?.legendBlueDesc || 'overall trend (helps visualize general progression)'}</div>
                )}
              </div>
            </div>
            </div>
          </div>
        )}
      </div>

      {/* Other Charts Section Premium */}
      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-6 mb-6">
        {/* Audiogram Comparison Premium */}
        {timeseries.audiogram_baseline.length > 0 && timeseries.audiogram_latest.length > 0 && (
          <div className="md:col-span-2 relative overflow-hidden rounded-3xl bg-white/5 backdrop-blur-[8px] border border-white/10 shadow-[0_10px_40px_rgba(0,0,0,0.55)] px-5 py-5">
            {/* Overlay gradient subtil */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#3B48EE]/15 via-transparent to-[#6F42EA]/10 pointer-events-none" />
            
            <div className="relative">
              <div className="flex items-center justify-between mb-1">
                <h4 className="text-lg text-white font-semibold tracking-wide">
                  {t?.audiometricEvolution || 'Audiometric Evolution'}
                </h4>
                <HelpIcon 
                  title={t?.audiometricEvolution || 'Audiometric Evolution'}
                  content={
                    <p>{t?.audiometricHelpText || 'Compare votre premier test (pointillés) et votre dernier test (lignes pleines). Permet de suivre l\'évolution globale de votre audition.'}</p>
                  }
                  size={18}
                />
              </div>
              <p className="text-white/70 text-sm mb-4 tracking-wide">
                {t?.audiogramComparison || 'Comparison between first test (dashed line) and latest (solid line)'}
              </p>
              
              <div className="bg-[#0F1525] rounded-2xl border border-white/10 shadow-[0_6px_28px_rgba(0,0,0,0.45)] p-4">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                    <XAxis 
                      dataKey="frequency"
                      type="number"
                      domain={['dataMin', 'dataMax']}
                      scale="log"
                      tickFormatter={(value) => `${value}`}
                      tick={{ fontSize: 12, fill: 'rgba(255,255,255,0.7)' }}
                      label={{ value: 'Hz', position: 'insideBottom', offset: -5, fill: 'rgba(255,255,255,0.7)' }}
                      stroke="rgba(255,255,255,0.3)"
                    />
                    <YAxis 
                      reversed 
                      domain={[0, 120]} 
                      tick={{ fontSize: 12, fill: 'rgba(255,255,255,0.7)' }}
                      label={{ value: 'dB HL', angle: -90, position: 'insideLeft', fill: 'rgba(255,255,255,0.7)' }}
                      stroke="rgba(255,255,255,0.3)"
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(255,255,255,0.1)', 
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '12px',
                        color: '#fff',
                        padding: '12px'
                      }}
                      labelStyle={{ color: 'rgba(255,255,255,0.8)' }}
                      formatter={(value) => `${value} dB HL`}
                      labelFormatter={(value) => `${value} Hz`}
                    />
                    <Legend wrapperStyle={{ color: '#fff' }} />
                  
                  {/* Baseline audiogram data - dashed lines */}
                  <Line 
                    data={timeseries.audiogram_baseline}
                    type="monotone" 
                    dataKey="threshold_left" 
                    stroke="#93c5fd" 
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    name={t?.baselineLeft || 'Baseline Left'}
                    dot={{ r: 3 }}
                  />
                  <Line 
                    data={timeseries.audiogram_baseline}
                    type="monotone" 
                    dataKey="threshold_right" 
                    stroke="#c4b5fd" 
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    name={t?.baselineRight || 'Baseline Right'}
                    dot={{ r: 3 }}
                  />
                  
                  {/* Current audiogram data - solid lines */}
                  <Line 
                    data={timeseries.audiogram_latest}
                    type="monotone" 
                    dataKey="threshold_left" 
                    stroke="#3b82f6" 
                    strokeWidth={3}
                    name={t?.currentLeft || 'Current Left'}
                    dot={{ r: 4 }}
                  />
                  <Line 
                    data={timeseries.audiogram_latest}
                    type="monotone" 
                    dataKey="threshold_right" 
                    stroke="#8b5cf6" 
                    strokeWidth={3}
                    name={t?.currentRight || 'Current Right'}
                    dot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* Tinnitus Evolution Charts - Separated for clarity */}
        {timeseries.tinnitus_frequency && timeseries.tinnitus_frequency.length >= 1 && (() => {
          // Combine frequency and volume data by date, sorted chronologically
          const evolutionData = timeseries.tinnitus_frequency.map((freqPoint, index) => {
            const volumePoint = timeseries.tinnitus_volume?.find(v => v.date === freqPoint.date);
            const dateObj = new Date(freqPoint.date);
            
            // Format date with time to distinguish multiple tests on same day
            const dateStr = dateObj.toLocaleDateString();
            const timeStr = dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            
            return {
              id: `test-${dateObj.getTime()}-${index}`, // UNIQUE identifier for each test
              date: dateStr,
              dateTime: `${dateStr} ${timeStr}`, // Full datetime for display
              dateSort: dateObj.getTime(),
              timestamp: dateObj.getTime(), // Exact timestamp in ms
              frequency: freqPoint.value,
              volume: volumePoint?.value || null
            };
          }).sort((a, b) => a.dateSort - b.dateSort);
          
          const hasVolumeData = evolutionData.some(d => d.volume !== null);
          
          return (
            <>
              {/* Volume Chart Premium */}
              {hasVolumeData && (
                <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#121721] to-[#1B2334] border border-white/8 shadow-[0_8px_40px_rgba(72,52,212,0.25)] px-6 py-6">
                  {/* Overlay gradient subtil */}
                  <div className="absolute inset-0 bg-gradient-to-b from-[#3B48EE]/8 via-transparent to-[#6F42EA]/6 pointer-events-none" />
                  
                  <div className="relative">
                    {/* Header avec icône aide */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Ear className="w-5 h-5 text-[#FF9B7F] opacity-85" />
                        <h4 className="text-white font-semibold text-lg tracking-wide">
                          {t?.tinnitusVolumeEvolution || 'Évolution du Volume des Acouphènes'}
                        </h4>
                      </div>
                      
                      {/* Icône aide premium */}
                      <HelpIcon 
                        title={t?.tinnitusVolumeEvolution || 'Tinnitus Volume Evolution'}
                        content={
                          <div>
                            <p className="font-semibold mb-2">Volume Evolution</p>
                            <p>{t?.tinnitusVolumeHelpText || 'Votre estimation du volume en dB SPL. Des variations sont normales au jour le jour.'}</p>
                          </div>
                        }
                        size={18}
                      />
                    </div>
                    
                    <p className="text-white/55 text-sm mb-4">
                      {t?.tinnitusVolumeDesc || 'Volume perçu en dB SPL - Objectif : diminution au fil du temps'}
                    </p>
                    
                    {/* Graph container premium */}
                    <div className="bg-[#0F1420] rounded-2xl border border-white/8 p-4">
                      <ResponsiveContainer width="100%" height={280}>
                      <LineChart data={evolutionData} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.12)" />
                        <XAxis 
                          dataKey="timestamp"
                          type="number"
                          domain={['dataMin', 'dataMax']}
                          tickFormatter={(timestamp) => {
                            const date = new Date(timestamp);
                            const dateStr = date.toLocaleDateString();
                            const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                            return `${dateStr} ${timeStr}`;
                          }}
                          label={{ value: t?.date || 'Date', position: 'insideBottom', offset: -10, fill: 'rgba(255,255,255,0.75)' }}
                          tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.75)', fontWeight: 500 }}
                          angle={-25}
                          textAnchor="end"
                          height={70}
                          stroke="rgba(255,255,255,0.3)"
                        />
                        <YAxis 
                          label={{ value: 'Volume (dB SPL)', angle: -90, position: 'insideLeft', fill: 'rgba(255,255,255,0.75)', dx: -10 }}
                          domain={[0, 'dataMax + 3']}
                          tick={{ fill: 'rgba(255,255,255,0.75)', fontWeight: 500 }}
                          stroke="rgba(255,255,255,0.3)"
                          width={60}
                        />
                        <Tooltip 
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const data = payload[0].payload;
                              return (
                                <div className="bg-[#131A27] backdrop-blur-xl p-3 border border-white/20 rounded-xl shadow-[0_4px_18px_rgba(0,0,0,0.45)]">
                                  <p className="font-semibold text-sm text-white mb-2">{data.dateTime}</p>
                                  {data.volume !== null && (
                                    <p className="text-sm text-[#F97373] font-medium">
                                      Volume: {data.volume.toFixed(1)} dB SPL
                                    </p>
                                  )}
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Legend 
                          verticalAlign="top" 
                          align="center"
                          wrapperStyle={{ paddingBottom: '10px', color: '#fff' }}
                          iconType="line"
                        />
                        <Line 
                          type="monotone" 
                          dataKey="volume" 
                          stroke="#F97373" 
                          strokeWidth={4}
                          name={t?.perceivedVolume || "Volume perçu (dB SPL)"}
                          dot={{ 
                            r: 7, 
                            fill: '#F97373', 
                            stroke: '#fff', 
                            strokeWidth: 2.2,
                            filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.3))'
                          }}
                          connectNulls={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}

              {/* Frequency Chart Premium */}
              <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#121721] to-[#1B2334] border border-white/8 shadow-[0_8px_40px_rgba(72,52,212,0.25)] px-6 py-6">
                {/* Overlay gradient subtil */}
                <div className="absolute inset-0 bg-gradient-to-b from-[#3B48EE]/8 via-transparent to-[#6F42EA]/6 pointer-events-none" />
                
                <div className="relative">
                  {/* Header avec icône aide */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Ear className="w-5 h-5 text-[#FF9B7F] opacity-85" />
                      <h4 className="text-white font-semibold text-lg tracking-wide">
                        {t?.tinnitusFrequencyEvolution || 'Évolution de la Fréquence des Acouphènes'}
                      </h4>
                    </div>
                    
                    {/* Icône aide premium */}
                    <HelpIcon 
                      title={t?.tinnitusFrequencyEvolution || 'Tinnitus Frequency Evolution'}
                      content={
                        <div>
                          <p className="font-semibold mb-2">Frequency Evolution</p>
                          <p>{t?.tinnitusFrequencyHelpText || 'Votre fréquence estimée en Hz. De légères variations sont normales.'}</p>
                        </div>
                      }
                      size={18}
                    />
                  </div>
                  
                  <p className="text-white/55 text-sm mb-4">
                    {t?.tinnitusFrequencyDesc || 'Fréquence détectée en Hz au fil du temps'}
                  </p>
                  
                  {/* Graph container premium */}
                  <div className="bg-[#0F1420] rounded-2xl border border-white/8 p-4">
                    <ResponsiveContainer width="100%" height={280}>
                      <LineChart data={evolutionData} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.12)" />
                        <XAxis 
                          dataKey="timestamp"
                          type="number"
                          domain={['dataMin', 'dataMax']}
                          tickFormatter={(timestamp) => {
                            const date = new Date(timestamp);
                            const dateStr = date.toLocaleDateString();
                            const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                            return `${dateStr} ${timeStr}`;
                          }}
                          label={{ value: t?.date || 'Date', position: 'insideBottom', offset: -10, fill: 'rgba(255,255,255,0.75)' }}
                          tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.75)', fontWeight: 500 }}
                          angle={-25}
                          textAnchor="end"
                          height={70}
                          stroke="rgba(255,255,255,0.3)"
                        />
                        <YAxis 
                          label={{ value: t?.frequencyHz || 'Fréquence (Hz)', angle: -90, position: 'insideLeft', fill: 'rgba(255,255,255,0.75)', dx: -15 }}
                          domain={['dataMin - 500', 'dataMax + 500']}
                          tick={{ fill: 'rgba(255,255,255,0.75)', fontWeight: 500 }}
                          stroke="rgba(255,255,255,0.3)"
                          width={70}
                        />
                      <Tooltip 
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <div className="bg-[#131A27] backdrop-blur-xl p-3 border border-white/20 rounded-xl shadow-[0_4px_18px_rgba(0,0,0,0.45)]">
                                <p className="font-semibold text-sm text-white mb-2">{data.dateTime}</p>
                                <p className="text-sm text-[#FDBA74] font-medium">
                                  {t?.frequency || 'Fréquence'}: {data.frequency.toFixed(0)} Hz
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Legend 
                        verticalAlign="top" 
                        align="center"
                        wrapperStyle={{ paddingBottom: '10px', color: '#fff' }}
                        iconType="line"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="frequency" 
                        stroke="#FDBA74" 
                        strokeWidth={3}
                        name={t?.frequencyHz || "Fréquence (Hz)"}
                        dot={{ 
                          r: 6, 
                          fill: '#FDBA74', 
                          stroke: '#fff', 
                          strokeWidth: 2.2,
                          filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.3))'
                        }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </>
          );
        })()}
      </div>

      {/* Insights Section */}
      <div className="max-w-6xl mx-auto mb-6">
        <div className="relative overflow-hidden rounded-3xl bg-white/5 backdrop-blur-[8px] border border-white/10 shadow-[0_10px_40px_rgba(0,0,0,0.55)] px-5 py-5 space-y-3">
          {/* Overlay gradient subtil */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#3B48EE]/15 via-transparent to-[#6F42EA]/10 pointer-events-none" />
          
          <div className="relative">
            <h4 className="text-lg flex items-center gap-2 text-white font-semibold tracking-wide mb-2">
              <Activity className="w-5 h-5" />
              {t?.insightsTitle || 'Insights & Recommendations'}
            </h4>
            
            {/* Context disclaimer - premium non-medical tone */}
            <p className="text-xs text-white/50 mb-4 italic">
              {lang === 'fr' 
                ? 'Ces indications décrivent des tendances estimées — pas un diagnostic.'
                : 'These insights describe estimated trends — not a diagnosis.'}
            </p>
            
            <div className="space-y-3">
              {summary.insights.map((insight, index) => (
                <div
                  key={index}
                  className={`flex items-start gap-3 rounded-2xl px-4 py-3 backdrop-blur-md ${
                    insight.type === 'positive' 
                      ? 'bg-green-500/12 border border-green-400/25' 
                      : insight.type === 'recommendation'
                      ? 'bg-white/10 border border-white/15'
                      : 'bg-white/10 border border-white/15'
                  }`}
                >
                  <span className="bg-white/15 backdrop-blur-md rounded-full p-1.5 text-white">
                    {getInsightIcon(insight.type)}
                  </span>
                  <p className={`flex-1 text-sm ${insight.type === 'positive' ? 'text-white/90' : 'text-white/85'}`}>
                    {lang === 'fr' ? insight.text_fr : insight.text_en}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Mini-onboarding hint Premium */}
      {showVariationHint && (
        <div className="fixed bottom-28 left-1/2 transform -translate-x-1/2 z-50 max-w-md w-full mx-4 pb-safe">
          <div className="rounded-2xl shadow-2xl p-5 border bg-white/10 backdrop-blur-xl border-white/20" style={{
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
          }}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-[#3B48EE]/30 backdrop-blur-md">
                <span className="text-xl">ℹ️</span>
              </div>
              <div className="flex-1">
                <p className="text-sm leading-relaxed text-white/90">
                  {t?.onboardingVariationsText || 
                    'Your tinnitus may fluctuate from day to day — this is completely normal. The dashboard focuses mainly on your long-term progress.'}
                </p>
                <button
                  onClick={handleDismissVariationHint}
                  className="mt-3 px-4 py-2 rounded-xl text-sm font-medium transition-all bg-gradient-to-br from-[#3B48EE] to-[#6F42EA] text-white hover:brightness-110 active:scale-[0.97]"
                >
                  {t?.ok || 'OK'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Progress;
